import { type Application, applications } from '@labeling/globals';
import { Button, Combobox, Modal, TextField } from '@lego/connect-components-react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { TableSkeleton } from '@/components/table-skeleton';
import { Table } from '../../components';
import type { TableColumn } from '../../components/table';
import { useApi } from '../../providers/api';
import { useToastContext } from '../../providers/toast';
import { useUserStore } from '../../stores/user-store';
import type { GraphicAssignmentData, GraphicData } from '../../types/types';
import { fetchAllPages } from '../../utils/pagination';
import { assertResponseOk, assertSettledResponsesOk, toErrorMessage } from '../../utils/response';

export const GraphicsPage = () => {
  const { t } = useTranslation();
  const { client } = useApi();
  const { isAdminOrMaintainer } = useUserStore();
  const { showToast } = useToastContext();
  const [selectedGraphic, setSelectedGraphic] = useState<GraphicData | null>(null);
  const [assignments, setAssignments] = useState<string[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [portalTarget, setPortalTarget] = useState<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLElement>(null);

  const {
    data: graphics,
    error: graphicsError,
    isLoading: graphicsIsLoading,
    mutate: reloadGraphics,
  } = useSWR('graphics', () =>
    fetchAllPages(async (query) => {
      const response = await client.v1.graphics.$get({ query });
      await assertResponseOk(response, t('graphicsPage.response.graphicsLoadFailed'));
      return response.json();
    })
  );
  const {
    data: graphicAssignments,
    error: assignmentsError,
    isLoading: assignmentsIsLoading,
    mutate: reloadAssignments,
  } = useSWR('graphic-assignments', () =>
    fetchAllPages(async (query) => {
      const response = await client.v1['graphic-assignments'].$get({ query });
      await assertResponseOk(response, t('graphicsPage.response.graphicsLoadFailed'));
      return response.json();
    })
  );

  const assignmentOptions = useMemo(
    () => applications.map((application) => ({ value: application, label: application })),
    []
  );

  const getGraphicAssignments = useCallback(
    (graphicId: number) =>
      graphicAssignments?.items.filter(
        (assignment: GraphicAssignmentData) => assignment.graphicId === graphicId
      ) ?? [],
    [graphicAssignments?.items]
  );

  const formatAssignments = useCallback(
    (graphicId: number) => {
      const graphicAssignmentsForGraphic = getGraphicAssignments(graphicId);
      return graphicAssignmentsForGraphic.length === 0
        ? '-'
        : graphicAssignmentsForGraphic.map((assignment) => assignment.application).join(', ');
    },
    [getGraphicAssignments]
  );

  const handleGraphicClick = (graphic: GraphicData) => {
    triggerRef.current = document.activeElement as HTMLElement;
    setSelectedGraphic(graphic);
    setAssignments(getGraphicAssignments(graphic.id!).map((assignment) => assignment.application));
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => setIsDialogOpen(false);

  const handleSave = async () => {
    if (!selectedGraphic || !isAdminOrMaintainer()) return;

    const failedMessage = t('graphicsPage.response.graphicUpdateFailed');
    const refreshCaches = () => Promise.all([reloadGraphics(), reloadAssignments()]);

    try {
      const currentAssignments = getGraphicAssignments(selectedGraphic.id!);
      const newAssignments = assignments.filter(
        (application) =>
          !currentAssignments.some((assignment) => assignment.application === application)
      );
      const removedAssignments = currentAssignments.filter(
        (assignment) => !assignments.includes(assignment.application)
      );

      await assertSettledResponsesOk(
        [
          ...newAssignments.map((application) =>
            client.v1['graphic-assignments'].$post({
              json: { graphicId: selectedGraphic.id!, application: application as Application },
            })
          ),
          ...removedAssignments.map((assignment) =>
            client.v1['graphic-assignments'][':id'].$delete({
              param: { id: assignment.id.toString() },
            })
          ),
        ],
        failedMessage
      );

      await refreshCaches();
      showToast(t('graphicsPage.response.graphicUpdatedSuccess'), 'success');
      handleCloseDialog();
    } catch (error) {
      showToast(toErrorMessage(error, failedMessage), 'error');
      // Partial saves may have succeeded; refresh so a retry doesn't repeat them.
      await refreshCaches().catch(() => undefined);
    }
  };

  const tableColumns: TableColumn<GraphicData>[] = useMemo(
    () => [
      {
        key: 'name',
        label: t('graphicsPage.display.name'),
        sortable: true,
        render: (value) => <span className="text-core-slate-1300 font-medium">{value}</span>,
      },
      {
        key: 'description',
        label: t('graphicsPage.display.description'),
        sortable: true,
        accessor: (graphic) => graphic.description || '-',
      },
      {
        key: 'path',
        label: t('graphicsPage.display.path'),
        sortable: true,
        accessor: (graphic) => graphic.externalPath,
      },
      {
        key: 'id',
        label: 'ID',
        sortable: true,
        accessor: (graphic) => graphic.id?.toString() ?? '-',
      },
      {
        key: 'availableRevisions',
        label: t('graphicsPage.display.revisions'),
        sortable: true,
        accessor: (graphic) => graphic.availableRevisions?.length.toString() || '0',
      },
      {
        key: 'applications',
        label: t('graphicsPage.display.applications'),
        sortable: false,
        accessor: (graphic) => formatAssignments(graphic.id!),
      },
      {
        key: 'created',
        label: t('graphicsPage.display.created'),
        sortable: true,
        accessor: (graphic) =>
          graphic.createdAt ? new Date(graphic.createdAt).toLocaleDateString() : '-',
      },
      {
        key: 'updated',
        label: t('graphicsPage.display.updated'),
        sortable: true,
        accessor: (graphic) =>
          graphic.updatedAt ? new Date(graphic.updatedAt).toLocaleDateString() : '-',
      },
    ],
    [formatAssignments, t]
  );

  if (!graphics || !graphicAssignments || graphicsIsLoading || assignmentsIsLoading) {
    if (graphicsError || assignmentsError) {
      const error = graphicsError ?? assignmentsError;
      return (
        <div className="flex items-center justify-center h-full" role="alert">
          {error instanceof Error ? error.message : t('graphicsPage.response.graphicsLoadFailed')}
        </div>
      );
    }

    return <TableSkeleton columns={tableColumns.length} page showAction />;
  }

  return (
    <>
      <div className="px-400 py-400">
        <h1 className="text-heading-md font-brand font-bold text-core-slate-1300 mb-300">
          {`${t('graphicsPage.display.graphicsTitle')} (${graphics.itemCount})`}
        </h1>
        <Table
          data={graphics.items}
          columns={tableColumns}
          onRowClick={handleGraphicClick}
          loading={false}
          emptyMessage={t('graphicsPage.display.noGraphicsFound')}
          searchPlaceholder={t('graphicsPage.display.searchGraphicsPlaceholder')}
          ariaLabel={t('graphicsPage.display.graphicsTitle')}
        />
      </div>

      {selectedGraphic && (
        <Modal
          isOpen={isDialogOpen}
          onClose={handleCloseDialog}
          triggerRef={triggerRef}
          size="medium"
        >
          <Modal.Title>
            {isAdminOrMaintainer()
              ? t('graphicsPage.action.editGraphicTitle', { name: selectedGraphic.name })
              : t('graphicsPage.action.viewGraphicTitle', { name: selectedGraphic.name })}
          </Modal.Title>
          <Modal.Content>
            <div
              ref={setPortalTarget}
              className="grid grid-cols-1 sm:grid-cols-2 gap-200 text-body-xs"
            >
              <div className="sm:col-span-2">
                <span className="font-brand font-bold text-core-slate-1100 block mb-50">
                  {t('graphicsPage.display.name')}:
                </span>
                <TextField
                  value={selectedGraphic.name}
                  size="small"
                  readOnly
                  className="opacity-60"
                />
              </div>
              <div className="sm:col-span-2">
                <span className="font-brand font-bold text-core-slate-1100 block mb-50">
                  {t('graphicsPage.display.description')}:
                </span>
                <TextField
                  value={selectedGraphic.description || ''}
                  size="small"
                  readOnly
                  className="opacity-60"
                />
              </div>
              <div className="max-w-full break-words">
                <span className="font-brand font-bold text-core-slate-1100 block mb-50">
                  {t('graphicsPage.display.pathLabel')}
                </span>
                <p className="text-core-slate-1300">{selectedGraphic.externalPath}</p>
              </div>
              <div>
                <span className="font-brand font-bold text-core-slate-1100 block mb-50">
                  {t('graphicsPage.display.revisionsLabel')}
                </span>
                <p className="text-core-slate-1300">
                  {selectedGraphic.availableRevisions?.join(', ') || '-'}
                </p>
              </div>
              <div className="sm:col-span-2">
                <span className="font-brand font-bold text-core-slate-1100 block mb-50">
                  {t('graphicsPage.display.applicationsLabel')}
                </span>
                <Combobox
                  fullWidth
                  value={assignments}
                  onChange={(_event, items) => setAssignments(items.map((item) => item.value))}
                  placeholder={t('graphicsPage.display.selectApplications')}
                  size="small"
                  disabled={!isAdminOrMaintainer()}
                  multiple
                  {...(portalTarget ? { portalTarget } : {})}
                >
                  {assignmentOptions.map((option) => (
                    <Combobox.Option key={option.value} value={option.value}>
                      {option.label}
                    </Combobox.Option>
                  ))}
                </Combobox>
              </div>
              <div>
                <span className="font-brand font-bold text-core-slate-1100">
                  {t('graphicsPage.display.createdLabel')}
                </span>
                <p className="text-core-slate-1300">
                  {new Date(selectedGraphic.createdAt).toLocaleDateString()}
                </p>
              </div>
              <div>
                <span className="font-brand font-bold text-core-slate-1100">
                  {t('graphicsPage.display.updatedLabel')}
                </span>
                <p className="text-core-slate-1300">
                  {new Date(selectedGraphic.updatedAt).toLocaleDateString()}
                </p>
              </div>
            </div>
          </Modal.Content>
          <Modal.Footer>
            <Button variant="neutral" size="small" onClick={handleCloseDialog}>
              {isAdminOrMaintainer()
                ? t('graphicsPage.action.cancel')
                : t('graphicsPage.action.close')}
            </Button>
            {isAdminOrMaintainer() && (
              <Button variant="primary" size="small" onClick={handleSave}>
                {t('graphicsPage.action.saveChanges')}
              </Button>
            )}
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};
